import { useEffect, useState } from 'react'

/**
 * Reports whether the on-screen keyboard is currently covering the viewport.
 *
 * Mobile browsers do not resize the layout viewport when the keyboard opens —
 * they shrink the *visual* viewport and leave `position: fixed` elements
 * anchored to the old bottom edge. That is what puts a bottom navigation bar
 * on top of the field someone is typing into.
 *
 * There is no keyboard event to listen for, so the height difference between
 * the two viewports is the only honest signal. A threshold well above browser
 * chrome (which also shrinks the visual viewport when it collapses) keeps this
 * from firing on an ordinary scroll.
 */
export function useKeyboardOpen(): boolean {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const viewport = typeof window !== 'undefined' ? window.visualViewport : undefined
    if (!viewport) return

    function read() {
      if (!viewport) return
      const covered = window.innerHeight - viewport.height - viewport.offsetTop
      setOpen(covered > 160)
    }

    read()
    viewport.addEventListener('resize', read)
    viewport.addEventListener('scroll', read)
    return () => {
      viewport.removeEventListener('resize', read)
      viewport.removeEventListener('scroll', read)
    }
  }, [])

  return open
}
